import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import QRCode_Facil from '../../assets/QRCode_Facil.png';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 15,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  section: {
    marginBottom: 20,
    padding: 12,
    border: '1pt solid #ddd',
    borderRadius: 6,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#2c3e50',
    borderBottom: '1pt solid #ccc',
    paddingBottom: 5,
    textTransform: 'uppercase',
  },
  field: {
    fontSize: 10,
    marginBottom: 4,
    color: '#444',
    lineHeight: 1.5,
  },
  boldText: {
    fontWeight: 'bold',
    color: '#000',
  },
  qrCodeSection: {
    fontSize: 10,
    textAlign: 'right',
  },
  qrCodeImage: {
    width: 70,
    height: 70,
    marginBottom: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 40,
    left: 40,
    right: 40,
    fontSize: 10,
    textAlign: 'center',
  },
  footerSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  footerField: {
    fontSize: 8,
    marginBottom: 2,
    flex: 1,
    paddingHorizontal: 5,
    paddingVertical: 3,
    border: '1pt solid #ddd',
    borderRadius: 4,
    backgroundColor: '#f7f7f7',
  },
  signatureLine: {
    marginTop: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  signatureText: {
    marginTop: 1,
    textAlign: 'center',
    fontSize: 12,
  },
});

const PrescriptionPDF = ({ data }) => {
  const {
    nomePaciente,
    cpfPaciente,
    enderecoPaciente, // Desestruturando apenas de `data`
    titulo,
    nome,
    dataAtendimento,
    crm,
    estadoCrm,
    validadeReceita,
    medicamento,
    formaUso,
    tokenReceita, // Agora o tokenReceita é recebido como prop
    clinica,
    posologia,
    observacao,
    tipoReceita,
    nomeComprador,
    cpfComprador,
    enderecoComprador,
  } = data;

  const tituloReceita =
    tipoReceita === 'especial'
      ? 'Receituário Controle Especial'
      : 'Receituário Médico';

  const codigoAcesso = crm; // Últimos 4 dígitos do CRM

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text>{tituloReceita}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Identificação do Emitente</Text>
          <Text style={styles.field}>{`${titulo} ${nome}`}</Text>
          <Text style={styles.field}>
            CRM {crm} - {estadoCrm}
          </Text>
          <Text style={styles.field}>
            Endereço Comercial: {clinica.endereco}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informações da Receita</Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={styles.field}>
                <Text style={styles.boldText}>Emissão:</Text> {dataAtendimento}
              </Text>
              <Text style={styles.field}>
                <Text style={styles.boldText}>Paciente:</Text> {nomePaciente}
              </Text>
              <Text style={styles.field}>
                <Text style={styles.boldText}>CPF do Paciente:</Text>{' '}
                {cpfPaciente}
              </Text>
              <Text style={styles.field}>
                <Text style={styles.boldText}>Endereço:</Text>{' '}
                {enderecoPaciente}
              </Text>
            </View>
            <View style={styles.qrCodeSection}>
              <Image style={styles.qrCodeImage} src={QRCode_Facil} />
              <Text>Token da receita: {tokenReceita}</Text>{' '}
              {/* Use o tokenReceita aqui */}
              <Text>Código de acesso: {codigoAcesso}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Prescrição Médica</Text>
          <Text style={styles.field}>{`Apresentação: ${formaUso}`}</Text>
          <Text
            style={styles.field}
          >{`Nome do Medicamento e Quantidade: ${medicamento}`}</Text>
          <Text style={styles.field}>{`Posologia: ${posologia}`}</Text>
          <Text
            style={styles.field}
          >{`Validade da Receita: ${validadeReceita}`}</Text>
          {observacao && (
            <Text style={styles.field}>{`Observações: ${observacao}`}</Text>
          )}
        </View>

        {tipoReceita === 'especial' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Identificação do Comprador</Text>
            <Text style={styles.field}>Nome: {nomeComprador}</Text>
            <Text style={styles.field}>CPF: {cpfComprador}</Text>
            <Text style={styles.field}>Endereço: {enderecoComprador}</Text>
          </View>
        )}

        <View style={styles.signatureLine}></View>
        <Text style={styles.signatureText}>Assinatura do Médico</Text>
        <Text style={styles.signatureText}>{`CRM: ${crm} - ${estadoCrm}`}</Text>

        <View style={styles.footer}>
          <Text>
            ESTE DOCUMENTO FOI ASSINADO DIGITALMENTE COM CERTIFICADO DIGITAL
          </Text>
          <Text>
            PADRÃO ICP-BRASIL AMPARADO PELA MEDIDA PROVISÓRIA 2200-2/2001
          </Text>
          <View style={styles.footerSection}>
            <View style={styles.footerField}>
              <Text>Identificação do Comprador</Text>
            </View>
            <View style={styles.footerField}>
              <Text>Identificação do Fornecedor</Text>
              <Text>Assinatura do farmacêutico</Text>
              <Text>Data</Text>
            </View>
          </View>
          <Text style={{ marginTop: 10 }}>
            Farmacêutico, valide a receita digital em
            https://prontuariofacil.com.br/receita-digital
          </Text>
        </View>
      </Page>
    </Document>
  );
};
PrescriptionPDF.propTypes = {
  data: PropTypes.shape({
    nomePaciente: PropTypes.string.isRequired,
    cpfPaciente: PropTypes.string.isRequired,
    enderecoPaciente: PropTypes.string.isRequired,
    titulo: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
    dataAtendimento: PropTypes.string.isRequired,
    crm: PropTypes.string.isRequired,
    estadoCrm: PropTypes.string.isRequired,
    validadeReceita: PropTypes.string.isRequired,
    medicamento: PropTypes.string.isRequired,
    formaUso: PropTypes.string.isRequired,
    tokenReceita: PropTypes.string.isRequired,
    clinica: PropTypes.shape({
      endereco: PropTypes.string.isRequired,
    }).isRequired,
    posologia: PropTypes.string.isRequired,
    observacao: PropTypes.string,
    tipoReceita: PropTypes.string.isRequired,
    nomeComprador: PropTypes.string,
    cpfComprador: PropTypes.string,
    enderecoComprador: PropTypes.string,
    enderecoComercial: PropTypes.string,
    cidadeComercial: PropTypes.string,
    estadoComercial: PropTypes.string,
    cepComercial: PropTypes.string,
  }).isRequired,
};

export default PrescriptionPDF;
